/**
 * MathContent - Componente para renderizar contenido HTML con fórmulas matemáticas
 * Convierte imágenes de fórmulas (SVG base64) a MathML puro para que MathJax las renderice
 * Esto es más eficiente que cargar imágenes base64 pesadas
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { convertFormulaImagesToMathML, expandirDivisionDesdeGuardado } from './ckeditorUtils';

/**
 * Componente que renderiza HTML con soporte para fórmulas matemáticas
 * Convierte automáticamente imágenes de fórmulas a MathML para renderizado con MathJax
 * 
 * @param {string} html - Contenido HTML a renderizar
 * @param {string} className - Clases CSS adicionales
 * @param {object} style - Estilos inline adicionales
 * @param {string} as - Elemento HTML a usar (div, span, p, etc.)
 */
const MathContent = ({ 
    html, 
    className = '', 
    style = {}, 
    as: Component = 'div',
    ...props 
}) => {
    const containerRef = useRef(null);
    
    // Expandir divisiones comprimidas y convertir fórmulas a MathML
    const processedHtml = useMemo(() => {
        if (!html) return '';
        const withDivisions = expandirDivisionDesdeGuardado(html);
        return convertFormulaImagesToMathML(withDivisions);
    }, [html]);
    
    // Llamar a MathJax para renderizar el MathML después de montar
    useEffect(() => {
        if (containerRef.current) {
            // Pequeño delay para asegurar que el DOM esté listo
            const timer = setTimeout(() => {
                if (window.MathJax?.typesetPromise) {
                    window.MathJax.typesetPromise([containerRef.current]).catch(err => {
                        console.warn('MathJax typeset error:', err);
                    });
                } else if (window.MathJax?.Hub?.Queue) {
                    // Fallback para MathJax 2.x
                    window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, containerRef.current]);
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [processedHtml]);
    
    return (
        <Component
            ref={containerRef}
            className={className}
            style={style}
            dangerouslySetInnerHTML={{ __html: processedHtml }}
            {...props}
        />
    );
};

export default MathContent;

/**
 * Hook para procesar HTML con fórmulas matemáticas
 * Útil cuando no se puede usar el componente MathContent directamente
 * 
 * @param {string} html - HTML a procesar
 * @returns {string} - HTML con fórmulas convertidas a MathML
 */
export function useMathContent(html) {
    return useMemo(() => {
        if (!html) return '';
        return convertFormulaImagesToMathML(html);
    }, [html]);
}

/**
 * Función para procesar HTML y llamar a MathJax en un elemento
 * Útil para casos donde se necesita control manual
 * 
 * @param {HTMLElement} element - Elemento DOM a procesar
 */
export function typesetMathInElement(element) {
    if (element && window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise([element]).catch(err => {
            console.warn('MathJax typeset error:', err);
        });
    }
}
